import { reactive, ref } from 'vue'
import { openUrl } from '@tauri-apps/plugin-opener'
import { invoke } from '~/lib/tauriInvoke'
import { runAction } from '~/stores/storeErrorHandling'
import {
    McpAuthFlowState,
    McpAuthState,
    McpToolKind,
    McpToolPermission,
    SYSTEM_MCP_SERVER_ID,
    type AgentMcpServer,
    type AgentMcpServers,
    type AgentMcpServerTools,
    type AgentMcpTool,
    type McpAuthStart,
    type McpAuthStatus,
    type McpAuthMode,
    type McpCatalogEndpoint,
    type McpCatalogEntry,
    type McpCatalogPage,
    type McpCatalogQuery,
    type McpCatalogServer,
    type McpHeader,
    type McpOAuthClientMode,
    type McpProbeResult,
    type McpTaxonomyValue,
} from '~/types/agent-credentials'

export {
    McpAuthFlowState,
    McpAuthState,
    McpToolKind,
    McpToolPermission,
    SYSTEM_MCP_SERVER_ID,
} from '~/types/agent-credentials'
export type {
    AgentMcpServer,
    AgentMcpTool,
    McpAuthMode,
    McpCatalogEndpoint,
    McpCatalogEntry,
    McpCatalogPage,
    McpCatalogQuery,
    McpCatalogServer,
    McpHeader,
    McpOAuthClientMode,
    McpProbeResult,
    McpTaxonomyValue,
} from '~/types/agent-credentials'

export interface McpOAuthClientInput {
    client_id: string
    client_secret?: string
}

/** A new server: from the catalog (`catalogId`) or the owner's own, set up in the wizard. */
export interface McpServerInput {
    name: string
    url: string
    catalogId?: string
    oauthClient?: McpOAuthClientInput | null
    /** Own server only — a catalog server takes these from the catalog. */
    authMode?: McpAuthMode
    oauthClientMode?: McpOAuthClientMode
    headers?: McpHeader[]
}

export type McpConnectionStatus = 'idle' | 'checking' | 'connected' | 'error' | 'auth-required'

export interface McpToolsState {
    status: McpConnectionStatus
    tools: AgentMcpTool[]
    error?: string | null
}

export interface McpSignInState {
    status: 'waiting' | 'failed'
    error?: string | null
}

export interface AgentMcpState {
    loaded: boolean
    loading: boolean
    loadError: string | null
    systemEnabled: boolean
    systemHasToolIntents: boolean
    servers: AgentMcpServer[]
    /** Connection check result per server id (`SYSTEM_MCP_SERVER_ID` for the built-in one). */
    tools: Record<string, McpToolsState>
    /** Sign-in in progress (browser open) per server id. */
    signIn: Record<string, McpSignInState>
}

// Module-level (singleton) cache keyed by bot id — same reasoning as `useBots`: the bot panel is
// torn down on navigation, and switching between bots shouldn't refetch a list that only this
// client changes. Every mutation below updates the cache in place, so the tab reacts immediately.
const stateByBot = reactive<Record<string, AgentMcpState>>({})

// The catalog lives in the backend's database and is paged/searched there (OpenSearch), so only the
// small taxonomy dictionary (filter labels) is cached — once per session.
const taxonomy = ref<McpTaxonomyValue[]>([])
let taxonomyLoad: Promise<void> | null = null

// Sign-in polling timers, so logout (or a new sign-in for the same server) can stop them.
const signInTimers = new Map<string, ReturnType<typeof setTimeout>>()

/** How long we keep waiting for the owner to finish signing in in the browser (matches the backend). */
const SIGN_IN_TIMEOUT_MS = 10 * 60 * 1000
const SIGN_IN_POLL_MS = 2000

/** Logout: the next account must not see this one's MCP servers (see `clearUserSession`). */
export function resetAgentMcpServers() {
    for (const timer of signInTimers.values()) clearTimeout(timer)
    signInTimers.clear()
    for (const botId of Object.keys(stateByBot)) delete stateByBot[botId]
}

/**
 * «Разрешить всегда» on a tool approval made the backend flip this tool to ALWAYS_ALLOW. Patches the
 * cached tool list (if that server's tools were ever loaded) so the MCP tab doesn't keep showing
 * «Нужно одобрение»; nothing to do when it wasn't loaded — the next check fetches the real value.
 */
export function markAgentMcpToolAlwaysAllowed(botId: string, serverId: string, toolName: string) {
    const tools = stateByBot[botId]?.tools[serverId]
    if (!tools) return
    tools.tools = tools.tools.map((t) =>
        t.name === toolName ? { ...t, permission: McpToolPermission.ALWAYS_ALLOW } : t,
    )
}

function emptyState(): AgentMcpState {
    return {
        loaded: false,
        loading: false,
        loadError: null,
        systemEnabled: true,
        systemHasToolIntents: true,
        servers: [],
        tools: {},
        signIn: {},
    }
}

/** Client-side counterpart of the backend's `McpServerUrlPolicy` — instant feedback only, the server decides. */
export function validateMcpServerUrl(value: string): string | null {
    const url = value.trim()
    if (!url) return 'Укажите адрес MCP-сервера.'
    let parsed: URL
    try {
        parsed = new URL(url)
    } catch {
        return 'Некорректный адрес.'
    }
    if (parsed.protocol !== 'https:') return 'Адрес должен начинаться с https://.'
    if (parsed.username || parsed.password) return 'Логин и пароль в адресе не поддерживаются.'
    const host = parsed.hostname.replace(/\.$/, '').toLowerCase()
    if (host === 'localhost' || (!host.includes('.') && !host.includes(':'))) return 'Адрес указывает на внутреннюю сеть.'
    return null
}

/** Host from a catalog list: exact name or «*.domain» — any subdomain of domain (same as the backend). */
export function mcpHostMatches(host: string, allowed: string) {
    const h = host.toLowerCase()
    const a = allowed.toLowerCase()
    return a.startsWith('*.') ? h.endsWith(a.slice(1)) && h.length > a.length - 1 : h === a
}

/**
 * Client-side counterpart of `McpCatalogEndpoint.Allows`: whether an address fits a catalog server
 * (its only url, one of its options, one of its hosts, or its pattern). The backend decides.
 */
export function mcpEndpointAllows(endpoint: McpCatalogEndpoint, value: string): boolean {
    let url: URL
    try {
        url = new URL(value.trim())
    } catch {
        return false
    }
    switch (endpoint.type) {
        case 'fixed':
            return !!endpoint.url && sameMcpUrl(url.href, endpoint.url)
        case 'one_of':
            return endpoint.options.some((o) => sameMcpUrl(url.href, o.url))
        case 'user_provided':
            return endpoint.hosts.some((h) => mcpHostMatches(url.hostname, h))
        case 'user_provided_pattern':
            try {
                return !!endpoint.pattern && new RegExp(endpoint.pattern).test(url.href)
            } catch {
                return true // a pattern JS can't compile — let the backend decide
            }
    }
}

/** Address a catalog server connects to without asking (fixed / first option); null — the owner enters it. */
export function mcpDefaultUrl(endpoint: McpCatalogEndpoint): string | null {
    if (endpoint.type === 'fixed') return endpoint.url ?? null
    if (endpoint.type === 'one_of') return endpoint.options[0]?.url ?? null
    return null
}

/** Same normalisation the backend uses to spot «this catalog server is already added». */
export function sameMcpUrl(a: string, b: string) {
    return a.replace(/\/+$/, '').toLowerCase() === b.replace(/\/+$/, '').toLowerCase()
}

export const MCP_TOOL_KIND_ORDER: McpToolKind[] = [McpToolKind.INTERACTIVE, McpToolKind.READ_ONLY, McpToolKind.WRITE]

export const MCP_TOOL_KIND_LABELS: Record<McpToolKind, string> = {
    [McpToolKind.INTERACTIVE]: 'Интерактивные',
    [McpToolKind.READ_ONLY]: 'Только чтение',
    [McpToolKind.WRITE]: 'Изменение и удаление',
}

export const MCP_TOOL_PERMISSION_LABELS: Record<McpToolPermission, string> = {
    [McpToolPermission.ALWAYS_ALLOW]: 'Всегда разрешать',
    [McpToolPermission.NEEDS_APPROVAL]: 'Нужно одобрение',
    [McpToolPermission.BLOCKED]: 'Заблокировано',
}

export function useAgentMcpServers() {
    const noopSetError = () => {}

    function stateFor(botId: string): AgentMcpState {
        if (!stateByBot[botId]) stateByBot[botId] = emptyState()
        return stateByBot[botId]
    }

    function applySnapshot(state: AgentMcpState, snapshot: AgentMcpServers) {
        state.systemEnabled = snapshot.system_server_enabled
        state.systemHasToolIntents = snapshot.system_server_has_tool_intents
        state.servers = snapshot.servers
        state.loaded = true
        state.loadError = null
    }

    async function ensureLoaded(botId: string, force = false) {
        const state = stateFor(botId)
        if ((state.loaded && !force) || state.loading) return
        state.loading = true
        try {
            const snapshot = await runAction(
                (msg) => (state.loadError = msg),
                { action: 'list_agent_mcp_servers', extra: { botId }, rethrow: false },
                () => invoke<AgentMcpServers>('list_agent_mcp_servers', { botId }),
            )
            if (snapshot) applySnapshot(state, snapshot)
        } finally {
            state.loading = false
        }
    }

    /** One page of the catalog (search, filters, «Популярное» / «Рекомендуем»). Throws on failure. */
    function listCatalog(query: McpCatalogQuery): Promise<McpCatalogPage> {
        return invoke<McpCatalogPage>('list_mcp_catalog', { payload: query })
    }

    /** The full card of a catalog entry (by id or slug). */
    function getCatalogServer(idOrSlug: string, botId?: string | null): Promise<McpCatalogServer> {
        return invoke<McpCatalogServer>('get_mcp_catalog_server', { idOrSlug, botId: botId ?? null })
    }

    /** The owner's own servers (private catalog entries), newest first. */
    function listMine(botId?: string | null): Promise<McpCatalogPage> {
        return invoke<McpCatalogPage>('list_my_mcp_servers', { botId: botId ?? null })
    }

    /** Filter labels; a failure leaves the raw values as labels. */
    function ensureTaxonomy(): Promise<void> {
        taxonomyLoad ??= invoke<McpTaxonomyValue[]>('list_mcp_taxonomy')
            .then((values) => {
                taxonomy.value = values
            })
            .catch((e) => {
                console.error('Failed to load the MCP taxonomy', e)
                taxonomyLoad = null
            })
        return taxonomyLoad
    }

    /** Checks a server before adding it: connection, sign-in settings, sign-in provider, name and icon. */
    function probeServer(url: string, headers: McpHeader[] = []): Promise<McpProbeResult> {
        return invoke<McpProbeResult>('probe_mcp_server', { url, headers })
    }

    function replaceServer(state: AgentMcpState, server: AgentMcpServer) {
        state.servers = state.servers.map((s) => (s.id === server.id ? server : s))
    }

    /** Connects to the server (backend-side) and lists its tools with their permissions. */
    async function checkServer(botId: string, serverId: string) {
        const state = stateFor(botId)
        const previous = state.tools[serverId]
        state.tools[serverId] = { status: 'checking', tools: previous?.tools ?? [] }
        try {
            const result = await invoke<AgentMcpServerTools>('get_agent_mcp_server_tools', { botId, serverId })
            state.tools[serverId] = {
                status: result.auth_required ? 'auth-required' : result.connected ? 'connected' : 'error',
                tools: result.tools,
                error: result.error,
            }
            // A manually added server that answered 401 is now known to need sign-in.
            if (result.auth_required) {
                const server = state.servers.find((s) => s.id === serverId)
                if (server && server.auth_state === McpAuthState.NOT_REQUIRED) {
                    replaceServer(state, { ...server, auth_state: McpAuthState.SIGN_IN_REQUIRED })
                }
            }
        } catch (e: any) {
            state.tools[serverId] = { status: 'error', tools: [], error: e?.message || 'Не удалось проверить сервер' }
        }
    }

    async function createServer(botId: string, input: McpServerInput): Promise<AgentMcpServer> {
        const state = stateFor(botId)
        const catalogId = input.catalogId
        const server = (await runAction(noopSetError, { action: 'create_agent_mcp_server', extra: { botId, catalogId } }, () =>
            invoke<AgentMcpServer>('create_agent_mcp_server', {
                payload: {
                    bot_id: botId,
                    name: input.name,
                    url: input.url,
                    catalog_id: catalogId ?? null,
                    oauth_client_id: input.oauthClient?.client_id || null,
                    oauth_client_secret: input.oauthClient?.client_secret || null,
                    auth_mode: input.authMode ?? null,
                    oauth_client_mode: input.oauthClientMode ?? null,
                    headers: input.headers ?? [],
                },
            }),
        )) as AgentMcpServer
        state.servers = [...state.servers, server]
        // Like Claude's connectors: the new server shows up at once and immediately reports whether it works —
        // or, if it needs an account, goes straight to sign-in.
        if (server.auth_state === McpAuthState.SIGN_IN_REQUIRED) void startSignIn(botId, server.id)
        else void checkServer(botId, server.id)
        return server
    }

    async function updateServer(
        botId: string,
        serverId: string,
        changes: {
            name?: string
            url?: string
            oauth_client_id?: string
            oauth_client_secret?: string
            auth_mode?: McpAuthMode
            oauth_client_mode?: McpOAuthClientMode
            /** With `headers` — replace the server's request headers (an empty list removes them). */
            replace_headers?: boolean
            headers?: McpHeader[]
        },
    ): Promise<AgentMcpServer> {
        const state = stateFor(botId)
        const server = (await runAction(noopSetError, { action: 'update_agent_mcp_server', extra: { botId, serverId } }, () =>
            invoke<AgentMcpServer>('update_agent_mcp_server', { payload: { bot_id: botId, server_id: serverId, ...changes } }),
        )) as AgentMcpServer
        replaceServer(state, server)
        // A new OAuth client drops the sign-in (tokens belong to the old client).
        if (changes.oauth_client_id !== undefined || changes.oauth_client_mode !== undefined) {
            state.tools[serverId] = { status: 'auth-required', tools: [] }
        } else if (changes.url !== undefined || changes.replace_headers || changes.auth_mode !== undefined) {
            void checkServer(botId, serverId)
        }
        return server
    }

    /** Optimistic: the switch flips at once and rolls back if the backend refuses. */
    async function setServerEnabled(botId: string, serverId: string, enabled: boolean) {
        const state = stateFor(botId)
        const current = state.servers.find((s) => s.id === serverId)
        if (!current || current.is_enabled === enabled) return
        replaceServer(state, { ...current, is_enabled: enabled })
        try {
            const server = (await runAction(noopSetError, { action: 'update_agent_mcp_server', extra: { botId, serverId } }, () =>
                invoke<AgentMcpServer>('update_agent_mcp_server', {
                    payload: { bot_id: botId, server_id: serverId, is_enabled: enabled },
                }),
            )) as AgentMcpServer
            replaceServer(state, server)
        } catch (e) {
            const latest = state.servers.find((s) => s.id === serverId)
            if (latest) replaceServer(state, { ...latest, is_enabled: !enabled })
            throw e
        }
    }

    /** Optimistic: the row disappears at once and comes back in place if the backend refuses. */
    async function deleteServer(botId: string, serverId: string) {
        const state = stateFor(botId)
        const index = state.servers.findIndex((s) => s.id === serverId)
        if (index === -1) return
        const removed = state.servers[index]!
        state.servers = state.servers.filter((s) => s.id !== serverId)
        stopSignInPolling(botId, serverId)
        try {
            await runAction(noopSetError, { action: 'delete_agent_mcp_server', extra: { botId, serverId } }, () =>
                invoke('delete_agent_mcp_server', { botId, serverId }),
            )
            delete state.tools[serverId]
            delete state.signIn[serverId]
        } catch (e) {
            const servers = [...state.servers]
            servers.splice(Math.min(index, servers.length), 0, removed)
            state.servers = servers
            throw e
        }
    }

    async function setSystemEnabled(botId: string, enabled: boolean) {
        const state = stateFor(botId)
        if (state.systemEnabled === enabled) return
        state.systemEnabled = enabled
        try {
            const snapshot = (await runAction(noopSetError, { action: 'set_agent_system_mcp_enabled', extra: { botId } }, () =>
                invoke<AgentMcpServers>('set_agent_system_mcp_enabled', { botId, enabled }),
            )) as AgentMcpServers
            applySnapshot(state, snapshot)
            if (state.tools[SYSTEM_MCP_SERVER_ID]) void checkServer(botId, SYSTEM_MCP_SERVER_ID)
        } catch (e) {
            state.systemEnabled = !enabled
            throw e
        }
    }

    /**
     * Optimistic: the chosen permission shows at once for every listed tool (a group change passes
     * all of its tools), and the previous values come back if the backend refuses.
     */
    async function setToolPermissions(botId: string, serverId: string, toolNames: string[], permission: McpToolPermission) {
        const state = stateFor(botId)
        const tools = state.tools[serverId]
        if (!tools || toolNames.length === 0) return
        const names = new Set(toolNames)
        const previous = new Map(tools.tools.filter((t) => names.has(t.name)).map((t) => [t.name, t.permission]))
        tools.tools = tools.tools.map((t) => (names.has(t.name) ? { ...t, permission } : t))
        try {
            await runAction(noopSetError, { action: 'set_agent_mcp_tool_permissions', extra: { botId, serverId } }, () =>
                invoke('set_agent_mcp_tool_permissions', {
                    payload: {
                        bot_id: botId,
                        server_id: serverId,
                        permissions: toolNames.map((tool_name) => ({ tool_name, permission })),
                    },
                }),
            )
        } catch (e) {
            const current = state.tools[serverId]
            if (current) {
                current.tools = current.tools.map((t) => (previous.has(t.name) ? { ...t, permission: previous.get(t.name)! } : t))
            }
            throw e
        }
    }

    function signInKey(botId: string, serverId: string) {
        return `${botId}:${serverId}`
    }

    function stopSignInPolling(botId: string, serverId: string) {
        const key = signInKey(botId, serverId)
        const timer = signInTimers.get(key)
        if (timer) clearTimeout(timer)
        signInTimers.delete(key)
    }

    async function finishSignIn(botId: string, serverId: string) {
        const state = stateFor(botId)
        stopSignInPolling(botId, serverId)
        delete state.signIn[serverId]
        const server = state.servers.find((s) => s.id === serverId)
        if (server) replaceServer(state, { ...server, auth_state: McpAuthState.SIGNED_IN })
        await checkServer(botId, serverId)
    }

    function failSignIn(botId: string, serverId: string, error?: string | null) {
        stopSignInPolling(botId, serverId)
        stateFor(botId).signIn[serverId] = { status: 'failed', error: error || 'Не удалось войти.' }
    }

    /**
     * OAuth sign-in: the backend prepares the provider's page (discovery, client registration, PKCE),
     * we open it in the system browser and poll until the provider sends the owner back to the
     * backend's callback. The server's tools appear as soon as the sign-in lands.
     */
    async function startSignIn(botId: string, serverId: string) {
        const state = stateFor(botId)
        stopSignInPolling(botId, serverId)
        state.signIn[serverId] = { status: 'waiting' }
        let start: McpAuthStart
        try {
            start = (await runAction(noopSetError, { action: 'start_agent_mcp_server_auth', extra: { botId, serverId } }, () =>
                invoke<McpAuthStart>('start_agent_mcp_server_auth', { botId, serverId }),
            )) as McpAuthStart
        } catch (e: any) {
            failSignIn(botId, serverId, e?.message)
            return
        }

        if (start.state === McpAuthFlowState.CONNECTED) {
            // The server let us in without an account after all.
            await finishSignIn(botId, serverId)
            return
        }
        if (start.state !== McpAuthFlowState.PENDING || !start.authorization_url) {
            failSignIn(botId, serverId, start.error)
            return
        }

        try {
            await openUrl(start.authorization_url)
        } catch (e: any) {
            failSignIn(botId, serverId, 'Не удалось открыть браузер для входа.')
            return
        }

        const deadline = Date.now() + SIGN_IN_TIMEOUT_MS
        const key = signInKey(botId, serverId)
        let openedUrl = start.authorization_url
        const poll = async () => {
            signInTimers.delete(key)
            if (!stateByBot[botId]?.signIn[serverId]) return // cancelled (logout / server deleted)
            let status: McpAuthStatus | null = null
            try {
                status = await invoke<McpAuthStatus>('get_agent_mcp_server_auth_status', { botId, serverId })
            } catch {
                // A transient failure shouldn't end a sign-in the owner is still busy with.
            }
            if (status?.state === McpAuthFlowState.CONNECTED) return finishSignIn(botId, serverId)
            if (status?.state === McpAuthFlowState.FAILED) return failSignIn(botId, serverId, status.error)
            if (status?.state === McpAuthFlowState.NONE) return failSignIn(botId, serverId, 'Вход прерван. Попробуйте ещё раз.')
            // The service restarted the sign-in (new state): the page the owner has open is stale — open the new one.
            if (status?.authorization_url && status.authorization_url !== openedUrl) {
                openedUrl = status.authorization_url
                void openUrl(openedUrl).catch((e) => console.error('Failed to reopen the sign-in page', e))
            }
            if (Date.now() > deadline) return failSignIn(botId, serverId, 'Время на вход истекло.')
            signInTimers.set(key, setTimeout(poll, SIGN_IN_POLL_MS))
        }
        signInTimers.set(key, setTimeout(poll, SIGN_IN_POLL_MS))
    }

    function cancelSignIn(botId: string, serverId: string) {
        stopSignInPolling(botId, serverId)
        delete stateFor(botId).signIn[serverId]
    }

    async function signOut(botId: string, serverId: string) {
        const state = stateFor(botId)
        const server = (await runAction(noopSetError, { action: 'sign_out_agent_mcp_server', extra: { botId, serverId } }, () =>
            invoke<AgentMcpServer>('sign_out_agent_mcp_server', { botId, serverId }),
        )) as AgentMcpServer
        replaceServer(state, server)
        state.tools[serverId] = { status: 'auth-required', tools: [], error: null }
    }

    return {
        taxonomy,
        stateFor,
        ensureLoaded,
        listCatalog,
        getCatalogServer,
        listMine,
        ensureTaxonomy,
        probeServer,
        checkServer,
        createServer,
        updateServer,
        setServerEnabled,
        deleteServer,
        setSystemEnabled,
        setToolPermissions,
        startSignIn,
        cancelSignIn,
        signOut,
    }
}
